import { useAtomValue } from "@effect/atom-react";
import { connectionTargetSharesClientMachine } from "@afar-research/client-runtime/connection";
import type { EnvironmentPresentation as BaseEnvironmentPresentation } from "@afar-research/client-runtime/state/presentation";
import type { EnvironmentId } from "@afar-research/contracts";
import * as Option from "effect/Option";
import { Atom } from "effect/unstable/reactivity";
import { useMemo } from "react";

import { hostEnvironment } from "../connection/hostEnvironment";
import {
  type ComposerEnvironmentMachine,
  composerEnvironmentMachine,
} from "../lib/composerFilesAndFoldersStrategy.logic";
import { environmentPresentations, useEnvironmentPresentation } from "./presentation";
import { primaryEnvironmentIdAtom } from "./primaryEnvironment";
import { useEnvironmentQuery } from "./query";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { usePreparedConnection } from "./session";

export interface EnvironmentPresentation extends BaseEnvironmentPresentation {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  readonly displayUrl: string | null;
}

function projectEnvironmentPresentation(
  environmentId: EnvironmentId,
  presentation: BaseEnvironmentPresentation,
): EnvironmentPresentation {
  return {
    ...presentation,
    environmentId,
    label: presentation.entry.target.label,
    // An SSH Environment has no address worth showing: it answers on a loopback port that only
    // exists while a forward is open, so the destination is what a row names instead.
    displayUrl:
      presentation.entry.target._tag === "SshConnectionTarget"
        ? null
        : presentation.entry.target.httpBaseUrl,
  };
}

export function useEnvironments() {
  const catalog = useAtomValue(hostEnvironment.environmentEntriesValueAtom);
  const networkStatus = useAtomValue(hostEnvironment.networkStatusValueAtom);
  const presentationById = useAtomValue(environmentPresentations.presentationsAtom);

  const environments = useMemo(
    () =>
      [...presentationById.entries()].map(([environmentId, presentation]) =>
        projectEnvironmentPresentation(environmentId, presentation),
      ),
    [presentationById],
  );

  return {
    isReady: catalog.isReady,
    networkStatus,
    environments,
    presentationById,
  };
}

export function usePrimaryEnvironmentId(): EnvironmentId | null {
  return useAtomValue(primaryEnvironmentIdAtom);
}

export function useEnvironment(
  environmentId: EnvironmentId | null,
): EnvironmentPresentation | null {
  const { presentation } = useEnvironmentPresentation(environmentId);
  return useMemo(
    () =>
      environmentId === null || presentation === null
        ? null
        : projectEnvironmentPresentation(environmentId, presentation),
    [environmentId, presentation],
  );
}

export function usePrimaryEnvironment(): EnvironmentPresentation | null {
  return useEnvironment(usePrimaryEnvironmentId());
}

export function useEnvironmentHttpBaseUrl(environmentId: EnvironmentId | null): string | null {
  const prepared = usePreparedConnection(environmentId);
  return Option.isSome(prepared) ? prepared.value.httpBaseUrl : null;
}

export function useEnvironmentConnectionState(environmentId: EnvironmentId) {
  return useEnvironmentQuery(hostEnvironment.stateAtom(environmentId));
}

const environmentMachineAtom = Atom.family((environmentId: EnvironmentId) =>
  Atom.make((get): ComposerEnvironmentMachine => {
    const presentation = get(environmentPresentations.presentationAtom(environmentId));
    return composerEnvironmentMachine({
      sharesClientMachine:
        presentation !== null && connectionTargetSharesClientMachine(presentation.entry.target),
      hostOs: presentation?.serverConfig?.environment.platform.os ?? null,
    });
  }).pipe(Atom.withLabel(`environment:machine:${environmentId}`)),
);

/**
 * Where the Environment runs relative to this client, which decides whether a
 * path on this client's disk names the same file there. A string atom, so a
 * connection-state change elsewhere in the presentation does not re-render readers.
 */
export function useEnvironmentMachine(environmentId: EnvironmentId): ComposerEnvironmentMachine {
  return useAtomValue(environmentMachineAtom(environmentId));
}

const environmentHostsBrowserAtom = Atom.family((environmentId: EnvironmentId) =>
  Atom.make(
    (get): boolean =>
      get(environmentPresentations.presentationAtom(environmentId))?.serverConfig?.environment
        .capabilities.collaborativeBrowser === "environment-hosted-v1",
  ).pipe(Atom.withLabel(`environment:hosts-browser:${environmentId}`)),
);

/**
 * Whether this Environment runs the Collaborative Browser itself and streams it
 * to every client (ADR-0137). A boolean atom, so presentation churn elsewhere
 * does not re-render the browser panel.
 */
export function useEnvironmentHostsBrowser(environmentId: EnvironmentId | null): boolean {
  return useAtomValue(
    environmentId === null ? noBrowserAtom : environmentHostsBrowserAtom(environmentId),
  );
}

const noBrowserAtom = Atom.make(false);

/** The same answer outside React, for link handlers and commands. */
export function readEnvironmentHostsBrowser(environmentId: EnvironmentId): boolean {
  return appAtomRegistry.get(environmentHostsBrowserAtom(environmentId));
}

const environmentHasCloudDisplayAtom = Atom.family((environmentId: EnvironmentId) =>
  Atom.make(
    (get): boolean =>
      get(environmentPresentations.presentationAtom(environmentId))?.serverConfig?.environment
        .capabilities.cloudDisplay === "rfb-v1",
  ).pipe(Atom.withLabel(`environment:cloud-display:${environmentId}`)),
);

/** Whether this Environment is a Cloud Workspace whose Cloud Display can be watched (ADR-0137). */
export function useEnvironmentHasCloudDisplay(environmentId: EnvironmentId | null): boolean {
  return useAtomValue(
    environmentId === null ? noBrowserAtom : environmentHasCloudDisplayAtom(environmentId),
  );
}
